import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Checkbox, Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogContent from "../dialog/functional/DialogContent";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },
    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function TkOutSourcingInputInspectionInputDialog(props) {
    const classes = useStyles();
    const { t } = useTranslation();

    const [instance, setInstance] = useState();
    const [interaction, setInteraction] = useState(true);
    const [formData, setFormData] = useState();
    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [keepOpen, setKeepOpen] = useState(false);

    const {
        // Dialog Properties

        id,
        title,
        platform,
        resizable,
        moveable,
        fullWidth,
        maxWidth,
        onInitialized,
        noKeepOpen,

        // Form Properties

        formId,
        schema,
        initialData,
        setInitialDataCallback,
        modifyFormDataCallback,
        checkShowCallback,
        checkDisabledCallback,
        checkFormValidationCallback,
        checkItemValidationCallback,
        modifyUrlCallback,

        // customize error message callback

        customizeErrorMsgCallback,

        // AutoFormDialog Properties

        onClose,
        onChange,
        onSuccess,
        onFailure,

        // Do Not Notify Using Snackbar by Dialog itself

        doNotNotify,
        doNotNotifyFailure,
        doNotNotifySuccess,

        doNotUseKeyAsUrlWhenUpdate,

        ...others
    } = props;

    const [tableSchema, setTableSchema] = useState(schema);

    useEffect(() => {
        if (schema) {
            let columnMap = {};

            for (const column of schema.columns) {
                columnMap[column.name] = column;
            }

            schema.columnMap = columnMap;
        }

        setTableSchema(schema);
    }, [schema]);

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    const notifyWarning = (msg) => {
        console.warn(msg);

        if (!(doNotNotify || doNotNotifyFailure)) {
            setNotification({
                severity: "warning",
                msg: msg,
                open: true,
            });
        }
    };

    const notifyFailure = (msg) => {
        console.log("notifyFailure");

        if (!(doNotNotify || doNotNotifyFailure)) {
            setNotification({
                severity: "error",
                msg: msg,
                open: true,
            });
        }
    };

    const notifySuccess = (msg) => {
        console.log("notifySuccess");

        if (doNotNotifySuccess === false) {
            setNotification({
                severity: "success",
                msg: msg,
                open: true,
            });
        }
    };

    const onCloseDialog = () => {
        if (onClose) {
            onClose();
        }
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onDialogInitialized = (instance) => {
        setInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onChangeFormData = (formData) => {
        setTimeout(() => {
            setFormData(formData);

            if (onChange) {
                onChange(formData);
            }
        }, 0);
    };

    const onApiSuccess = (result) => {
        notifySuccess(t("success.inputSuccess"));

        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                instance.hide();
            }

            return keepOpen;
        });

        if (onSuccess) {
            setTimeout(() => onSuccess(result.data), 0);
        }
    };

    const onApiFailure = (reason) => {
        console.log(reason);

        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t("error.doInputFailure", {
                    reason: reason.response ? reason.response.data : JSON.stringify(reason),
                })
            );
        }

        if (onFailure) {
            onFailure(reason, notifyFailure);
        }
    };

    const onOk = (formData, schema) => {
        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (valid && checkFormValidationCallback) {
                [valid, msg] = checkFormValidationCallback(formData);
            }

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url = "/api/" + schema.name;

        if (modifyUrlCallback) {
            url = modifyUrlCallback(url);
        }

        if (!doNotUseKeyAsUrlWhenUpdate) {
            for (const column of schema.columns) {
                if (column.key === "PRI") {
                    url += "/" + formData[column.name];
                }
            }
        }

        if (uiConf.updateWithPatch || schema.updateWithPatch) {
            let apiData = {};

            // transform api data for file type from here

            if (initialData) {
                // build api data only for modified columns

                for (const columnName in formData) {
                    if (
                        initialData[columnName] !== formData[columnName] &&
                        !(
                            schema.columnMap &&
                            schema.columnMap[columnName] &&
                            schema.columnMap[columnName].doNotSubmitOnApi
                        )
                    ) {
                        apiData[columnName] = formData[columnName];
                    }
                }
            } else {
                apiData = { ...formData };
            }

            // transform api data for file type to here

            let form = new FormData();
            let json = {};
            let deleted = {};
            let updateCount = 0;

            for (const formItem in apiData) {
                if (
                    schema.columnMap &&
                    schema.columnMap[formItem] &&
                    schema.columnMap[formItem].formItemType === "file"
                ) {
                    deleted[formItem] = apiData[formItem].deletedFiles;

                    if (deleted[formItem]) {
                        updateCount++;
                    }

                    for (const file of apiData[formItem].activeFiles) {
                        if (file.type === "url") continue;

                        form.append(formItem, file.file);
                        updateCount++;
                    }
                } else {
                    if (apiData[formItem] !== undefined) {
                        json[formItem] = apiData[formItem];
                        updateCount++;
                    }
                }
            }

            if (!updateCount) {
                notifyWarning(t("warning.noChange"));
                setInteraction(false);
                return;
            }

            Object.keys(deleted).length !== 0 && form.append("deleted", JSON.stringify(deleted));
            form.append("json", JSON.stringify(json));

            apiClient
                .patch(url, form, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((result) => onApiSuccess(result))
                .catch((reason) => onApiFailure(reason));
        } else {
            let apiData = {};

            if (initialData) {
                // build api data only for modified columns

                for (const columnName in formData) {
                    if (schema.columnMap && !schema.columnMap[columnName]) {
                        continue;
                    }

                    if (
                        !(
                            schema.columnMap &&
                            schema.columnMap[columnName] &&
                            schema.columnMap[columnName].doNotSubmitOnApi
                        )
                    ) {
                        apiData[columnName] = formData[columnName];
                    }
                }
            } else {
                apiData = { ...formData };
            }

            let form = new FormData();
            let json = {};
            let deleted = {};

            for (const formItem in apiData) {
                if (
                    schema.columnMap &&
                    schema.columnMap[formItem] &&
                    schema.columnMap[formItem].formItemType === "file"
                ) {
                    deleted[formItem] = apiData[formItem].deletedFiles;

                    for (const file of apiData[formItem].activeFiles) {
                        if (file.type === "url") continue;

                        form.append(formItem, file.file);
                    }
                } else {
                    if (apiData[formItem] !== undefined) {
                        json[formItem] = apiData[formItem];
                    }
                }
            }

            form.append("deleted", JSON.stringify(deleted));
            form.append("json", JSON.stringify(json));

            apiClient
                .put(url, form, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((result) => onApiSuccess(result, schema))
                .catch((reason) => onApiFailure(reason));
        }
    };

    const onCancel = () => {
        instance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    return (
        <Dialog
            id={id}
            title={title}
            platform={platform ? platform : uiConf.dialogPlatform}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth={maxWidth ? maxWidth : uiConf.autoFormDialogMaxWidth}
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            {uiConf.modalWithCloseButton ? (
                <DialogHeaderWithClose>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeaderWithClose>
            ) : (
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
            )}

            <DialogContent dividers>
                <AutoForm
                    id={formId}
                    schema={tableSchema}
                    mode={"edit"}
                    initialData={initialData}
                    setInitialDataCallback={setInitialDataCallback}
                    modifyFormDataCallback={modifyFormDataCallback}
                    checkShowCallback={checkShowCallback}
                    checkDisabledCallback={checkDisabledCallback}
                    checkValidationCallback={checkItemValidationCallback}
                    onChange={onChangeFormData}
                />

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <DialogActionsOkCancel
                firstActionComponent={noKeepOpen ? () => <></> : checkKeepOpen}
                //labels={t("common.ok")}
                buttonDisabled={[!interaction, false]}
                onOk={() => onOk(formData, tableSchema)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default TkOutSourcingInputInspectionInputDialog;
